<template>
  <div container>
    <h2 class="_mbs">{{ title }}</h2>
    <div container>
      <div>
        <cloud-cannon-content
          class="_p0 bg-light-gray"
          :content="cloudCannonContent.header_summary"
          :content-loading="cloudCannonContentLoading"
        />
      </div>
      <async-loader v-if="loading" />
      <div v-else>
        <!-- Broker/TPA/Client view -->
        <div v-if="!isInternalUser">
          <h3 class="_mbs">Confirm Access for {{ parentGroupName }}</h3>
          <div
            id="card"
            v-for="(attestation, idx) in attestations"
            :key="`att_${idx}`"
          >
            <div id="blurb">
              <cloud-cannon-content
                class="_p0 bg-light-gray"
                :content="cloudCannonContent.attestations[attestation.attestations.toLowerCase()].attestation_statement"
                :content-loading="cloudCannonContentLoading"
              />
            </div>
            <div id="choices">
              <attestation-choice
                :attestation="attestation"
                :cloudCannonContent="cloudCannonContent"
                :cloudCannonContentLoading="cloudCannonContentLoading"
                :perm="attestation.attestations.toLowerCase()"
                @choice-made="onChoiceMade"
              />
            </div>
          </div>
          <div>
            <cloud-cannon-content
              class="_p0 bg-light-gray"
              :content="cloudCannonContent.final_summary"
              :content-loading="cloudCannonContentLoading"
            />
          </div>
          <div id="btn-tray">
            <button
              type="button"
              class="btn --sm white"
              @click="onCancelClick"
            >Return to List</button>
            <button
              type="button"
              class="btn --sm"
              @click="onAttestClick"
              :disabled="saveButtonDisabled"
            >{{ buttonText }}</button>
          </div>
        </div>
        <!-- Internal View -->
        <div v-else>
          <h3 class="_mbs">Review Attestation Request for {{ parentGroupName }}</h3>
          <div id="card">
            <div class="int-card-content">
              <div class="int-card-row">
                <div class="int-card-label">User ID:</div>
                <div class="int-card-item">{{ internalSummary.clientId }}</div>
              </div>
              <div class="int-card-row">
                <div class="int-card-label">Parent Group:</div>
                <div class="int-card-item">{{ internalSummary.parentGroupName }}</div>
              </div>
              <div class="int-card-row">
                <div class="int-card-label">Originating User:</div>
                <div class="int-card-item">{{ internalSummary.originatingUserId }}</div>
              </div>
              <div class="int-card-row">
                <div class="int-card-label">Submitted On:</div>
                <div class="int-card-item">{{ internalSummary.submittedDt }}</div>
              </div>
              <div class="int-card-row">
                <div class="int-card-label">Status:</div>
                <div class="int-card-item int-sentence">{{ internalSummary.status }}</div>
              </div>
              <div class="int-card-row">
                <div class="int-card-label">Requested Access:</div>
                <div class="int-card-item int-sentence">{{ internalSummary.perms }}</div>
              </div>
            </div>
          </div>
          <div>
            <cloud-cannon-content
              class="_p0 bg-light-gray"
              :content="cloudCannonContent.final_summary"
              :content-loading="cloudCannonContentLoading"
            />
          </div>
          <div id="btn-tray">
            <button
              type="button"
              class="btn --sm white"
              @click="onCancelClick"
            >Return to List</button>
            <button
              type="button"
              class="btn --sm"
              @click="onAttestClick"
              :disabled="saveButtonDisabled"
            >{{ buttonText }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeMount, ref, watch } from '@vue/composition-api';
import { useRouter, useStore } from '@vue3shims';
import AsyncLoader from '@molecules/AsyncLoader';
import AttestationChoice from './AttestationChoice.vue';
import { useHiddenRouteParams } from '@composables/useHiddenRouteParams';

export default {
  setup(props) {
    // basics
    const store = useStore();
    const router = useRouter();
    const { hiddenRouteParams } = useHiddenRouteParams();

    //route params
    const id = ref(hiddenRouteParams.value.parentGroupCk);
    const originatingUserId = ref(hiddenRouteParams.value.originatingUserId);
    const userId = ref(hiddenRouteParams.value.userId);

    // vars
    const attestations = computed(() => store.getters['attestationDetail']);
    const buttonText = ref('');
    const cloudCannonContent = computed(() => store.getters['cloudCannon/cloudCannonContent']);
    const cloudCannonContentLoading = computed(() => store.getters['cloudCannon/cloudCannonContentLoading']);
    const cloudCannonResource = ref('');
    const detailFetcher = ref('');
    const isInternalUser = computed(() => store.getters['isInternalUser']);
    const loading = computed(() => store.getters['attestationsLoading']);
    const myAttestations = ref([]);
    const payloadType = ref('');
    const submitAction = ref('');
    const title = ref('');
    const updateKey = ref('');

    const parentGroupName = computed(() => {
      if (store.getters['attestationDetail'].length > 0) {
        return store.getters['attestationDetail'][0].parentGroupName;
      }
      else {
        return '';
      }
    });

    const internalSummary = computed(() => {
      let summary = {};
      let perms = '';
      for (let i = 0; i < attestations.value.length; i++) {
        let att = attestations.value[i];
        summary.clientId = att.clientId;
        summary.parentGroupId = att.parentGroupId;
        summary.parentGroupCk = att.parentGroupCk;
        summary.parentGroupName = att.parentGroupName;
        summary.originatingUserId = att.originatingUserId;
        summary.submittedDt = att.submittedDt;
        let status = att.status.replace('_', ' ');
        if (status === 'PENDING BROKER') {
          summary.status = 'Pending Review'
        } else {
          summary.status = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
        }
        let perm = att.attestations.charAt(0).toUpperCase() + att.attestations.slice(1).toLowerCase();
        perms += perm;
        if (i !== attestations.value.length - 1) {
          perms += ', ';
        }
      }

      summary.perms = perms;
      return summary;
    });

    const saveButtonDisabled = computed(() => {
      if(myAttestations.value.length < 1) {
        return true;
      }
      // never disabled for internal
      if(store.getters['isInternalUser']) {
        return false;
      }

      let shouldDisable = false;
      myAttestations.value.forEach((att) => {
        if(att[updateKey.value] === null) {
          shouldDisable = true;
        }
      });
      return shouldDisable;
    });

    const onChoiceMade = (choice) => {
      myAttestations.value.forEach((att) => {
        if(att.attestations === choice.perm.toUpperCase()) {
          att[updateKey.value] = choice.value;
        }
      });
      myAttestations.value = [...myAttestations.value];
    }

    const fetchData = async (opts) => {
      console.log(`detailFetcher`, detailFetcher.value);
      await store.dispatch(detailFetcher.value, opts);
      await store.dispatch('cloudCannon/GET_CLOUD_CANNON_CONTENT', { cloudCannonResource: cloudCannonResource.value });
    }

    const onAttestClick = async () => {
      let payload = myAttestations.value;
      try {
        await store.dispatch(submitAction.value, payload);
        router.push({name: 'attestations-list'});
      } catch (error) {
        console.error(error);
      }
    }

    const onCancelClick = () => {
      router.push({ name: 'attestations-list' });
    }

    const setupData = () => {
      let opts = {};
      if (['B', 'T'].includes(store.getters['accountType'])) {
        // console.log('is Broker');
        buttonText.value = ''
        title.value = 'Confirm Attestations';
        detailFetcher.value = 'GET_USER_ATTESTATIONS_BY_PARENT_GROUP';
        opts.parentGroupCk = id.value;
        cloudCannonResource.value = '/attestation-confirmation-request.json';
        buttonText.value = 'I attest the above choices are correct';
        payloadType.value = 'broker';
        submitAction.value = 'UPDATE_ATTESTATIONS';
        updateKey.value = 'brokerAttestInd';
      }
      else if (store.getters['isInternalUser']) {
        // console.log('is Internal');
        title.value = 'Review / Edit Attestations';
        detailFetcher.value = 'GET_INTERNAL_DETAIL';
        cloudCannonResource.value = '/attestation-detail-internal.json';
        opts.parentGroupCk = id.value;
        opts.clientId = userId.value;
        opts.originatingUserId = originatingUserId.value;
        buttonText.value = 'Cancel This Request';
        payloadType.value = 'internal';
        submitAction.value = 'CANCEL_ATTESTATION_REQUEST';
        updateKey.value = 'internalAttestInd';
      }
      else {
        // console.log('is Client');
        title.value = 'Approve Attestations';
        detailFetcher.value = 'GET_CLIENT_APPROVAL_DETAIL';
        opts.parentGroupCk = id.value;
        opts.userId = userId.value;
        cloudCannonResource.value = '/attestation_approve_request.json';
        buttonText.value = 'I approve the above attestations';
        payloadType.value = 'client';
        submitAction.value = 'CLIENT_APPROVE_ATTESTATIONS';
        updateKey.value = 'clientApprovalInd';
      }
      fetchData(opts);
    }

    onBeforeMount(async () => {
      setupData();
    });
    watch(id, (oVal, nVal) => {
      setupData();
    });
    watch(attestations, () => {
      myAttestations.value = attestations.value.map((att) => {
        att[updateKey.value] = null;
        return att;
      });
    });

    return {
      hiddenRouteParams,
      attestations,
      buttonText,
      cloudCannonContent,
      cloudCannonContentLoading,
      cloudCannonResource,
      internalSummary,
      isInternalUser,
      loading,
      onAttestClick,
      onCancelClick,
      onChoiceMade,
      parentGroupName,
      payloadType,
      saveButtonDisabled,
      submitAction,
      title,
    }
  },
  components: {
    AsyncLoader,
    AttestationChoice,
  }
}
</script>

<style scoped>
#card {
  border: 1px solid #aaaaaa;
  background-color: white;
  border-radius: 7px;
  padding: 0.5rem;
  margin: 0.5rem;
}

.int-card-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-evenly;
  width: 80%;
}

.int-card-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.int-card-item {
  padding-left: 1.2rem;
  font-weight: bold;
}

.int-sentence {
  text-transform: capitalize !important;
}

.int-card-label {
  width: 13rem;
  text-align: right;
}

#blurb {
  margin-bottom: 0.75rem;
}

#btn-tray {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
}
</style>
